/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import Select, { ActionMeta, OptionTypeBase, Props as SelectProps, StylesConfig, ValueType } from 'react-select';
import { BrandColors, Typography } from '@blend/enterprise-components';
import { getAssignmentCandidates } from 'blb-api/users';
import { MeContext } from 'blb-contexts/Me';
import { clearOpsDeskAssignee, setDefaultOpsDeskAssignee } from 'blb-utils/pipeline';
import { OpsDeskFilterParams } from 'blb-constants/pipelineFilter';

export interface SelectOption {
  label: string;
  value: string;
}

const domStyles = StyleSheet.create({
  selectWrapper: {
    width: '100%',
  },
});

const defaultLoadingMessage: ((obj: { inputValue: string }) => string | null) | undefined = () => 'Loading Agents…';
const defaultElementName = 'showAssigneeSelect';
const defaultPlaceholder = 'Show closings assigned to…';

const baseTextforSelect = {
  fontFamily: Typography.Roboto().fontFamily as string,
  fontSize: '0.75rem',
  fontWeight: 400,
  letterSpacing: '0.03em',
  lineHeight: '18px',
};

const defaultStyles: Partial<StylesConfig<SelectOption, false>> = {
  clearIndicator: provided => ({
    ...provided,
    ':hover': {
      color: BrandColors.SKY,
    },
    color: BrandColors.CLOUD_50,
  }),
  container: provided => ({
    ...provided,
    width: '100%',
  }),
  control: (provided, state) => {
    let borderColor = BrandColors.CLOUD_50;

    if (state.isFocused) {
      borderColor = BrandColors.SKY;
    }

    return {
      ...provided,
      ':hover': {
        borderColor: BrandColors.SKY,
      },
      borderColor,
      borderRadius: '3px',
      boxShadow: 'none',
    };
  },
  dropdownIndicator: provided => ({
    ...provided,
    ':hover': {
      color: BrandColors.SKY,
    },
    color: BrandColors.CLOUD_50,
  }),
  option: (provided, state) => {
    let backgroundColor = 'none';
    let color = BrandColors.NIGHT_80;

    if (state.isFocused) {
      backgroundColor = BrandColors.SKY;
      color = BrandColors.NIGHT_80;
    }

    if (state.isSelected) {
      backgroundColor = BrandColors.ACCENT_SKY_70;
      color = BrandColors.NIGHT_80;
    }

    if (state.isDisabled) {
      color = BrandColors.BLACK_25;
    }
    return {
      ...provided,
      ...baseTextforSelect,
      backgroundColor,
      color,
    };
  },
  placeholder: provided => ({
    ...provided,
    ...baseTextforSelect,
  }),
  valueContainer: provided => ({
    ...provided,
    ...baseTextforSelect,
    color: BrandColors.NIGHT_90,
  }),
};

export namespace PipelineFilterSelect {
  export interface Props extends SelectProps {
    filterUpdates: OpsDeskFilterParams;
    setFilterUpdates: React.Dispatch<React.SetStateAction<OpsDeskFilterParams>>;
  }
}

/**
 * Click handler to capture bubbled up event currently not addressed in React-Select
 * @param  {React.MouseEvent} event MouseEvent bubbled up from Select
 * @return {void}
 * @see: https://github.com/JedWatson/react-select/issues/2477
 * @function
 */
const stopImmediatePropagationHandler = (event: React.MouseEvent) => event.nativeEvent.stopImmediatePropagation();

export const PipelineFilterSelect: React.FC<PipelineFilterSelect.Props> = props => {
  const me = React.useContext(MeContext);
  const [getAssignmentCandidatesCalled, setGetAssignmentCandidatesCalled] = useState<boolean>(false);
  const [opsDeskAgents, setOpsDeskAgents] = useState<SelectOption[]>([]);

  const { filterUpdates, setFilterUpdates } = props;

  /**
   * React Effect to fetch Ops Desk Agents
   * based on if the call has been successfully made - stateful boolean.
   * @function
   */
  useEffect(() => {
    if (!getAssignmentCandidatesCalled) {
      fetchOpsDeskAgents();
    }
    if (getAssignmentCandidatesCalled && !opsDeskAgents.length) {
      // To make the user experience better, assignment-candidates returns an array with the logged in user at the top.
      setDefaultOpsDeskAssignee(filterUpdates, setFilterUpdates, opsDeskAgents[0], me);
    }
  }, []);

  /**
   * Callback for `useEffect`.
   * Fetches Agents from Users Internal Endpoint.
   * @function
   * @async
   */
  const fetchOpsDeskAgents = React.useCallback(async () => {
    // call api to get ops desk users
    const { data } = await getAssignmentCandidates(me!.principalId, true);

    setGetAssignmentCandidatesCalled(true);
    setOpsDeskAgents(data);
  }, [setOpsDeskAgents, setGetAssignmentCandidatesCalled]);

  /**
   * Change handler for Select
   * @param  {ValueType<OptionTypeBase, false>} option  Selected Option { label: string; value: string; }
   * @param  {ActionMeta<OptionTypeBase>} actionMeta    Description of Action taking place - Selecting/De-Selecting - etc
   * @return {void}
   * @function
   */
  const onSelectChange = (option: ValueType<OptionTypeBase, false>, actionMeta: ActionMeta<OptionTypeBase>) => {
    if (option !== null && actionMeta.action === 'select-option') {
      setFilterUpdates({
        ...filterUpdates,
        opsDeskAssigneeId: option.value,
        opsDeskAssignment: {
          name: option.label,
          principalId: option.value,
        },
      });
    }

    if (actionMeta.action === 'clear') {
      clearOpsDeskAssignee(filterUpdates, setFilterUpdates);
    }
  };

  let selectedOption = null;
  if (filterUpdates !== undefined && filterUpdates?.opsDeskAssignment !== undefined) {
    selectedOption = {
      label: filterUpdates.opsDeskAssignment.name,
      value: filterUpdates.opsDeskAssignment.principalId,
    };
  }
  // handle reset filter behavior, by updating selectedOption to loggedInUser
  if (selectedOption === null && opsDeskAgents.length > 0) {
    if (filterUpdates.opsDeskAssigneeId === me?.principalId) {
      selectedOption = {
        label: opsDeskAgents[0].label,
        value: opsDeskAgents[0].value,
      };
    }
  }

  const elementName = props.name ?? defaultElementName;
  const loadingMessage = props.loadingMessage ?? defaultLoadingMessage;
  const placeHolder = props.placeholder ?? defaultPlaceholder;
  const selectStyles = props.styles ?? defaultStyles;

  return (
    <div className={css(domStyles.selectWrapper)} onClick={stopImmediatePropagationHandler}>
      <Select
        isClearable={true}
        isLoading={!getAssignmentCandidatesCalled}
        loadingMessage={loadingMessage}
        name={elementName}
        onChange={onSelectChange}
        options={opsDeskAgents}
        placeholder={placeHolder}
        styles={selectStyles}
        value={selectedOption}
      />
    </div>
  );
};
